/**
 * Academics — ICA deep dive (pack slide 16).
 *
 * Seven ratios, each with a target, and a status the reader is meant to argue with.
 * Four decisions shape it.
 *
 *  1. TWO BASES, NEVER MIXED. The finalised pack measures lecturer capacity in full-time
 *     equivalents for one payroll month (`extras.ica_governance_fte`). The older block on
 *     the seat basis (`extras.ica_governance`: lecturer positions on the establishment) is
 *     still served until it is retired, so the panel reads the FTE block whenever it is
 *     present and falls back to the seat rendering only when it is not. The two are never
 *     drawn side by side: the same words ("ICA share") mean materially different numbers.
 *  2. THE SERVER OWNS THE VERDICT. On the FTE basis every metric carries `on_target`, and
 *     that is the status. The target string is rendered verbatim. On the seat basis the
 *     status is computed from `good` and the number inside the target, and a metric that
 *     cannot be read is "not assessable" rather than guessed at.
 *  3. NULL IS WITHHELD, ABSENT IS NOT AVAILABLE. A campus or faculty row withheld to
 *     protect a small group arrives with its FTE, share and people as null: it keeps its
 *     place, shows a dash, and is never worked back from the total or the other rows. The
 *     capacity outside the rows is taken as published, never computed as a difference.
 *  4. NOT A RECHARTS PANEL, deliberately. The cuts need a proportion bar with the ceiling
 *     drawn INSIDE the track and a breach flag the SERVER owns; the house BarRow grid gives
 *     both at a size that stays legible for thirteen rows.
 */
import React, { useMemo, type ReactNode } from 'react';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  Def,
  fmtDay,
  fmtNum,
  fmtOne,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type IcaGovernanceCutRow,
  type IcaGovernanceFte,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  /** Part of the shared panel signature. Neither basis follows the basis toggle — the
   *  lecturer figures are the same on all three — which is what the window line says. */
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

// =============================================================================
// SEAT-BASIS SHAPE — `extras.ica_governance` is served exactly as stored until it is
// retired, so its only reader types it here. The FTE block is typed in apiContract.
// =============================================================================

/** A published count. `null` means none was published — never zero. */
type Cell = number | null;

/** One metric row, wide enough for both bases: the FTE basis adds `key`, `threshold`,
 *  `on_target` and the concentration rows' `count` / `of`; the seat basis carries `note`. */
interface IcaMetric {
  key?: string | null;
  metric?: string | null;
  /** Rendered verbatim; parsed only for the number behind it. */
  target?: string | null;
  threshold?: number | null;
  value?: number | null;
  /** "%", "x", or "" for a bare ratio. */
  unit?: string | null;
  /** Which direction is better. */
  good?: string | null;
  /** The verdict published with the figure, where there is one. */
  on_target?: boolean | null;
  count?: number | null;
  of?: number | null;
  note?: string | null;
}

interface IcaCut {
  campus?: string | null;
  faculty?: string | null;
  lecturers?: Cell;
  ica?: Cell;
  ica_pct?: number | null;
  /** The SERVER owns the breach rule. The marker below only draws it. */
  breach?: boolean | null;
}

interface IcaGovernanceBlock {
  lecturers?: Cell;
  ica?: Cell;
  ftc?: Cell;
  perm?: Cell;
  students?: Cell;
  metrics?: IcaMetric[] | null;
  by_campus?: IcaCut[] | null;
  by_faculty?: IcaCut[] | null;
  caveat?: string | null;
}

function readIcaGovernance(panels: WorkforcePanels | null): IcaGovernanceBlock | null {
  return readExtras<IcaGovernanceBlock>(panels, 'ica_governance');
}

/**
 * Campus names for campus codes, from the campus footprint on the same response, so a row
 * can read "Tyger Valley (DV)" instead of "DV". Where no name is published for a code the
 * row stays as the bare code rather than inventing an expansion for it. The student ratio
 * and staff cost panels name their campuses through this too.
 */
export function readCampusNames(panels: WorkforcePanels | null | undefined): Record<string, string> {
  const list = readExtras(panels, 'campus_footprint')?.campuses;
  const out: Record<string, string> = {};
  (Array.isArray(list) ? list : []).forEach((c) => {
    const code = (c?.code ?? '').toString().trim();
    const name = (c?.name ?? '').toString().trim();
    if (code && name && name !== code) out[code] = name;
  });
  return out;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2026-07" -> "July". */
const monthName = (ym?: string | null): string | null => {
  const m = /^\d{4}-(\d{2})/.exec(ym ?? '');
  return m ? MONTHS[Number(m[1]) - 1] ?? null : null;
};

// =============================================================================
// PALETTE — hard-coded hex, same family as apiContract CATEGORY_COLORS. The status
// colours are SEMANTIC and deliberately distinct from the accent: a chip on this table is
// a verdict, not another series.
// =============================================================================

const ON_TARGET = '#0B7F72';
const ON_TARGET_BG = '#E7F2F0';
const OFF_TARGET = '#A8443A';
const OFF_TARGET_BG = '#F7EBE9';
const UNKNOWN_INK = '#5B6B85';
const UNKNOWN_BG = '#EEF1F6';

/** Within the concentration rule / over it. The bar takes the verdict's colour. */
const SHARE_OK = ACCENT; // #2E6FD9
const SHARE_BREACH = OFF_TARGET;
const TRACK = '#EEF1F6';
const MARKER = '#8A94A6';

/**
 * The seat basis carries no threshold of its own, so its ceiling is drawn from this
 * constant. Whether a row is in breach is still the served `breach` flag. The FTE basis
 * draws its ceiling from the served `threshold_pct`.
 */
const SEAT_BREACH_PCT = 55;

// =============================================================================
// TARGET PARSING
// =============================================================================

type Comparator = '<=' | '>=' | '<' | '>' | '=';
type Direction = 'low' | 'high';
type Status = 'on' | 'off' | 'unknown';

interface ParsedTarget {
  cmp: Comparator;
  threshold: number;
}

/** "<= 45%" | ">= 1.5" | "<= 150" | "≤ 45%". The number only — the value arrives in the
 *  same unit as its target by construction, so the unit on the string is not used. */
const TARGET_RE = /^\s*(<=|>=|≤|≥|<|>|=)?\s*(-?\d+(?:\.\d+)?)\s*(%|x|×)?\s*$/i;

function parseTarget(raw: string | null | undefined): ParsedTarget | null {
  if (raw === null || raw === undefined) return null;
  const m = TARGET_RE.exec(String(raw));
  if (!m) return null;
  const n = Number(m[2]);
  if (!Number.isFinite(n)) return null;
  const sym = m[1] ?? '=';
  const cmp: Comparator = sym === '≤' ? '<=' : sym === '≥' ? '>=' : (sym as Comparator);
  return { cmp, threshold: n };
}

/** `good` is authoritative. The comparator is only the fallback for a row that arrives
 *  without it — and a bare "= 45%" gives neither, so the direction stays null. */
function directionOf(metric: IcaMetric, parsed: ParsedTarget | null): Direction | null {
  const good = (metric.good ?? '').toString().trim().toLowerCase();
  if (good === 'low') return 'low';
  if (good === 'high') return 'high';
  if (parsed?.cmp === '<=' || parsed?.cmp === '<') return 'low';
  if (parsed?.cmp === '>=' || parsed?.cmp === '>') return 'high';
  return null;
}

const EPS = 1e-9;

/** On target INCLUDES sitting exactly on the number. A published verdict wins. */
function statusOf(metric: IcaMetric, parsed: ParsedTarget | null, dir: Direction | null): Status {
  if (typeof metric.value !== 'number' || !Number.isFinite(metric.value)) return 'unknown';
  if (typeof metric.on_target === 'boolean') return metric.on_target ? 'on' : 'off';
  if (!parsed || !dir) return 'unknown';
  if (dir === 'low') return metric.value <= parsed.threshold + EPS ? 'on' : 'off';
  return metric.value >= parsed.threshold - EPS ? 'on' : 'off';
}

// =============================================================================
// FORMATTING — the unit travels with the number, so a ratio never picks up a percent
// sign and a percentage-POINT gap is never printed as a percentage.
// =============================================================================

const isRatio = (unit: string | null | undefined) => (unit ?? '').toLowerCase() === 'x';
const isPct = (unit: string | null | undefined) => (unit ?? '') === '%';

function fmtMetricValue(v: number | null | undefined, unit: string | null | undefined): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  if (isPct(unit)) return `${v.toFixed(1)}%`;
  if (isRatio(unit)) return `${v.toFixed(2)}×`;
  return v.toFixed(1);
}

function fmtGap(d: number, unit: string | null | undefined): string {
  if (isPct(unit)) return `${d.toFixed(1)} pts`;
  if (isRatio(unit)) return `${d.toFixed(2)}×`;
  return d.toFixed(1);
}

function gapText(metric: IcaMetric, parsed: ParsedTarget | null, dir: Direction | null): string | null {
  if (typeof metric.value !== 'number' || !parsed || !dir) return null;
  const d = metric.value - parsed.threshold;
  if (Math.abs(d) < 0.005) return 'exactly on the target';
  const mag = fmtGap(Math.abs(d), metric.unit);
  if (dir === 'low') return d > 0 ? `${mag} above the ceiling` : `${mag} inside the ceiling`;
  return d < 0 ? `${mag} short of the floor` : `${mag} clear of the floor`;
}

/** A share on the seat basis, computed only when BOTH published parts are there. */
function shareOf(part: Cell | undefined, whole: Cell | undefined): number | null {
  if (typeof part !== 'number' || typeof whole !== 'number' || whole <= 0) return null;
  return (part / whole) * 100;
}

interface Assessed {
  m: IcaMetric;
  parsed: ParsedTarget | null;
  dir: Direction | null;
  status: Status;
  gap: string | null;
}

function assess(metrics: IcaMetric[]): Assessed[] {
  return metrics.map((m) => {
    const read = parseTarget(m.target);
    // A served threshold wins over the number read off the target string.
    const parsed: ParsedTarget | null =
      typeof m.threshold === 'number' && Number.isFinite(m.threshold)
        ? { cmp: read?.cmp ?? '=', threshold: m.threshold }
        : read;
    const dir = directionOf(m, parsed);
    return { m, parsed, dir, status: statusOf(m, parsed, dir), gap: gapText(m, parsed, dir) };
  });
}

// =============================================================================
// DEFINITIONS — server definitions win; the local map is consulted only when the lookup
// misses, so no heading on this panel is left without a tooltip.
// =============================================================================

const defBody = (label: string, body: string, caveat?: string): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveat ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>{caveat}</li>
      </ul>
    ) : null}
  </div>
);

/** Seat basis: lecturer positions on the establishment. */
const SEAT_DEFS: Record<string, ReactNode> = {
  ica_governance: defBody(
    'ICA governance',
    'The lecturer establishment split between independent contractors (ICA), permanent and fixed-term seats, with the ratios academic leadership manages against.',
    'Targets are preliminary and not yet benchmarked, so the status chips prompt a discussion rather than deliver a verdict.',
  ),
  ica_lecturer_positions: defBody(
    'Lecturer positions',
    'Approved lecturer seats on the establishment at the frame date, filled or vacant. A seat, not a person — one person can hold more than one seat, and a sessional lecturer is not an employee.',
  ),
  ica_positions: defBody(
    'ICA lecturer positions',
    'Lecturer seats held under an independent contractor agreement — sessional academic capacity, outside the employee basis entirely.',
  ),
  ica_perm_positions: defBody(
    'Permanent lecturer positions',
    'Lecturer seats on a permanent contract. With fixed-term, these are the employed teaching complement.',
  ),
  ica_ftc_positions: defBody(
    'Fixed-term lecturer positions',
    'Lecturer seats on a fixed-term contract. Fixed-term staff are employees and sit inside every employee figure on this page.',
  ),
  ica_students: defBody(
    'Invoiced students',
    'The invoiced student population behind both student ratios in the table below.',
    'From the Finance student file, so it moves independently of the establishment frame.',
  ),
  ica_metric_target: defBody(
    'Target',
    'The target as published, shown verbatim. The status beside it is computed from the direction the metric improves in — lower is better, or higher is better — with a value sitting exactly on the number counting as on target.',
    'Preliminary: not yet benchmarked against sector practice or against Eduvos history.',
  ),
  ica_metric_status: defBody(
    'Status',
    'On target where the current value sits on the right side of the published target, off target where it does not. Not assessable where the value is missing or the target cannot be read as a number — the panel will not guess a verdict.',
  ),
  ica_by_campus: defBody(
    'ICA share by campus',
    'Independent contractor seats as a percentage of all lecturer seats at that campus, ordered by share so the outliers lead.',
    'Campuses are flagged as published, not by this screen: the marker only draws the rule the flag applies.',
  ),
  ica_by_faculty: defBody(
    'ICA share by faculty',
    'Independent contractor seats as a percentage of all lecturer seats in that faculty, ordered by share.',
  ),
  ica_breach: defBody(
    'Over the ICA ceiling',
    `Flagged as carrying more than ${SEAT_BREACH_PCT}% ICA lecturer seats — the concentration rule academic leadership reviews against.`,
  ),
  ica_not_published: defBody(
    'Not published',
    'No figure is published for this cell. It shows as a dash rather than a zero, and it is left out of every total, maximum and percentage on this panel.',
  ),
};

/** FTE basis: lecturer capacity in one payroll month. */
function fteDefs(threshold: number, minCut: number): Record<string, ReactNode> {
  return {
    ica_governance_fte: defBody(
      'Academics — ICA deep dive',
      'Lecturer capacity in full-time equivalents for one payroll month: everyone paid that month who holds a lecturer position, split into independent contractors, permanent and fixed-term staff, with the ratios academic leadership manages against.',
      'An independent contractor counts as the share of a full-time equivalent their month’s cost represents, capped at one.',
    ),
    ica_lecturer_fte: defBody(
      'Lecturer capacity',
      'Full-time equivalents of everyone paid in the month who holds a lecturer position.',
    ),
    ica_fte_contractors: defBody(
      'Independent contractors',
      'Full-time equivalents of lecturers paid as independent contractors: each counts as the share of a full-time equivalent their month’s cost represents, capped at one.',
      'Independent contractors are not employees and appear in no employee figure on this page.',
    ),
    ica_fte_permanent: defBody(
      'Permanent',
      'Full-time equivalents of permanent lecturers paid in the month. Salaried staff count as one.',
    ),
    ica_fte_ftc: defBody(
      'Fixed-term (FTC)',
      'Full-time equivalents of fixed-term lecturers paid in the month. Fixed-term staff are employees.',
    ),
    ica_students: defBody(
      'Invoiced students',
      'Students invoiced in the payroll month, new and returning: the students behind the student ratios in the table below.',
    ),
    ica_metric_target: defBody('Target', 'The target as published, shown verbatim.'),
    ica_metric_status: defBody(
      'Status',
      'Whether the value meets its target, as published with the figure. Not assessable where no value is published.',
    ),
    ica_by_campus: defBody(
      'ICA share by campus',
      'Contractor full-time equivalents as a percentage of the lecturer full-time equivalents paid at that campus.',
      `A campus carrying fewer than ${minCut} lecturer full-time equivalents is not shown as a row; its capacity stays in the total.`,
    ),
    ica_by_faculty: defBody(
      'ICA share by faculty',
      'Contractor full-time equivalents as a percentage of the lecturer full-time equivalents paid, by the faculty the lecturer position is funded in.',
      `A faculty carrying fewer than ${minCut} lecturer full-time equivalents is not shown as a row; its capacity stays in the total.`,
    ),
    ica_breach: defBody(
      'Over the ICA ceiling',
      `More than ${threshold}% of the lecturer full-time equivalents are contractors.`,
    ),
    ica_withheld: defBody(
      'Withheld',
      'Withheld to protect small groups: fewer than five people. The row keeps its place, and its capacity stays in the total.',
    ),
    ica_not_published: defBody(
      'Not published',
      'No figure is published for this cell. It shows as a dash rather than a zero.',
    ),
  };
}

const Missing: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span className="inline-flex items-center justify-end text-gray-400" title="Not published">
    <span aria-hidden="true">—</span>
    <span className="sr-only">not published</span>
    <Def def={def} k="ica_not_published" />
  </span>
);

const Withheld: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span
    className="inline-flex items-center justify-end text-gray-400"
    title="Withheld to protect small groups"
  >
    <span aria-hidden="true">—</span>
    <span className="sr-only">withheld to protect small groups</span>
    <Def def={def} k="ica_withheld" />
  </span>
);

const NotAvailableForDate: React.FC<{ what: string }> = ({ what }) => (
  <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
    {what} is not available for this date.
  </div>
);

// =============================================================================
// STATUS CHIP
// =============================================================================

const STATUS_STYLE: Record<Status, { label: string; fg: string; bg: string }> = {
  on: { label: 'On target', fg: ON_TARGET, bg: ON_TARGET_BG },
  off: { label: 'Off target', fg: OFF_TARGET, bg: OFF_TARGET_BG },
  unknown: { label: 'Not assessable', fg: UNKNOWN_INK, bg: UNKNOWN_BG },
};

const StatusChip: React.FC<{ status: Status; title?: string }> = ({ status, title }) => {
  const s = STATUS_STYLE[status];
  return (
    <span
      title={title}
      className="inline-flex whitespace-nowrap rounded-full border px-2 py-[1px] text-[10.5px] font-semibold"
      style={{ color: s.fg, background: s.bg, borderColor: s.fg }}
    >
      {s.label}
    </span>
  );
};

function chipTitle(a: Assessed): string {
  if (a.status === 'unknown') {
    return 'No status: either no current value is published or the target could not be read as a number, so the panel does not guess one.';
  }
  const direction =
    a.dir === 'low'
      ? 'Lower is better on this metric, so a value at or under the target is on target.'
      : 'Higher is better on this metric, so a value at or above the target is on target.';
  return typeof a.m.on_target === 'boolean'
    ? `${direction} The status is the verdict published with the figure.`
    : direction;
}

// =============================================================================
// METRICS TABLE
// =============================================================================

const MetricsTable: React.FC<{
  assessed: Assessed[];
  def: DefLookup;
  noteOf: (m: IcaMetric) => string;
}> = ({ assessed, def, noteOf }) =>
  assessed.length ? (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="border-b text-gray-500">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Metric</th>
            <th className="px-2 py-1.5 text-right font-medium">
              <span className="inline-flex items-center justify-end">
                Target
                <Def def={def} k="ica_metric_target" />
              </span>
            </th>
            <th className="px-2 py-1.5 text-right font-medium">Current</th>
            <th className="px-2 py-1.5 text-left font-medium">
              <span className="inline-flex items-center">
                Status
                <Def def={def} k="ica_metric_status" />
              </span>
            </th>
            <th className="px-2 py-1.5 text-left font-medium">Note</th>
          </tr>
        </thead>
        <tbody>
          {assessed.map((a, i) => (
            <tr
              key={`${a.m.key ?? a.m.metric ?? 'metric'}-${i}`}
              className="border-b align-top last:border-0 hover:bg-gray-50"
            >
              <td className="px-2 py-2 text-gray-800">{a.m.metric ?? '—'}</td>
              <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-gray-600">
                {a.m.target?.toString().trim() || '—'}
              </td>
              <td className="whitespace-nowrap px-2 py-2 text-right font-semibold tabular-nums text-heritage-blue">
                {typeof a.m.value === 'number' ? fmtMetricValue(a.m.value, a.m.unit) : <Missing def={def} />}
              </td>
              <td className="px-2 py-2">
                <StatusChip status={a.status} title={chipTitle(a)} />
                {a.gap ? (
                  <div className="mt-0.5 text-[10.5px] leading-snug text-gray-500">{a.gap}</div>
                ) : null}
              </td>
              <td className="px-2 py-2 text-[11px] leading-snug text-gray-500">{noteOf(a.m)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
      No metric rows are published for this date, so no target can be assessed.
    </div>
  );

// =============================================================================
// SHARE ROW — label / proportion track with the ceiling drawn inside it / value
// =============================================================================

interface CutRow {
  key: string;
  label: ReactNode;
  pct: number | null;
  /** "12 of 40" (positions) or "12.0 of 40.0 FTE"; null when not both are published. */
  caption: string | null;
  breach: boolean;
  /** Withheld to protect a small group: a dash, never a share worked back. */
  withheld: boolean;
}

const ShareRow: React.FC<{ row: CutRow; threshold: number; def: DefLookup }> = ({
  row,
  threshold,
  def,
}) => (
  <div
    className="grid items-center gap-2.5 py-[3px] text-[12.5px]"
    style={{ gridTemplateColumns: 'minmax(0, 190px) 1fr 104px' }}
  >
    <div className="flex min-w-0 items-baseline gap-1.5">
      <span className="truncate text-gray-700">{row.label}</span>
      {row.breach ? (
        <span
          className="inline-flex shrink-0 items-center text-[9.5px] font-semibold uppercase tracking-wide"
          style={{ color: OFF_TARGET }}
        >
          over {threshold}%
          <Def def={def} k="ica_breach" />
        </span>
      ) : null}
    </div>
    <div className="relative h-[13px] overflow-hidden rounded-sm" style={{ background: TRACK }}>
      {row.pct !== null ? (
        <span
          className="absolute inset-y-0 left-0 rounded-sm transition-[width] duration-300"
          style={{
            width: `${Math.min(100, Math.max(0.8, row.pct))}%`,
            background: row.breach ? SHARE_BREACH : SHARE_OK,
          }}
        />
      ) : null}
      {/* The ceiling, drawn inside the track so every row is read against it. */}
      <span
        title={`${threshold}% ICA — the concentration ceiling`}
        className="absolute inset-y-0 w-px"
        style={{ left: `${Math.min(100, Math.max(0, threshold))}%`, background: MARKER }}
      />
    </div>
    <div className="text-right leading-tight">
      <div className="font-semibold tabular-nums text-heritage-blue">
        {row.withheld ? <Withheld def={def} /> : row.pct === null ? <Missing def={def} /> : fmtPct(row.pct)}
      </div>
      <div className="text-[10px] font-normal tabular-nums text-gray-400">{row.caption ?? ' '}</div>
    </div>
  </div>
);

/** One cut (campus or faculty), ordered by share so the outliers lead. */
const CutBlock: React.FC<{
  heading: string;
  defKey: string;
  rows: CutRow[];
  threshold: number;
  unit: string;
  def: DefLookup;
  footer?: ReactNode;
}> = ({ heading, defKey, rows, threshold, unit, def, footer }) => {
  const withheld = rows.filter((r) => r.withheld).length;
  const breaches = rows.filter((r) => r.breach).length;
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {heading}
          <Def def={def} k={defKey} />
        </div>
        <div className="text-[11px] text-gray-500">
          <b style={{ color: breaches ? OFF_TARGET : ON_TARGET }}>
            {breaches} of {rows.length - withheld}
          </b>{' '}
          {unit} over the {threshold}% ceiling
          {withheld ? ` · ${withheld} withheld to protect small groups` : ''} · the vertical marker
          is that ceiling
        </div>
      </div>
      {rows.map((r) => (
        <ShareRow key={r.key} row={r} threshold={threshold} def={def} />
      ))}
      {footer ? <div className="mt-1 text-[11px] leading-snug text-gray-500">{footer}</div> : null}
    </div>
  );
};

const byShare = (a: CutRow, b: CutRow) => (b.pct ?? -1) - (a.pct ?? -1);

// =============================================================================
// FTE BASIS — `extras.ica_governance_fte`
// =============================================================================

/** Rows exactly as published. A withheld row keeps its place with every figure dashed. */
function fteCut(
  list: IcaGovernanceCutRow[] | undefined,
  kind: 'campus' | 'faculty',
  names: Record<string, string>,
): CutRow[] {
  return (Array.isArray(list) ? list : [])
    .filter(Boolean)
    .map((r) => {
      const code = ((kind === 'campus' ? r.campus : r.faculty) ?? '').toString();
      const named = kind === 'campus' ? names[code] : undefined;
      const withheld = r.suppressed === true;
      return {
        key: code || `${kind}-unknown`,
        label: named ? (
          <>
            {named} <span className="text-gray-400">({code})</span>
          </>
        ) : (
          code || '—'
        ),
        pct: !withheld && typeof r.ica_pct === 'number' && Number.isFinite(r.ica_pct) ? r.ica_pct : null,
        caption:
          !withheld && typeof r.ica_fte === 'number' && typeof r.lecturer_fte === 'number'
            ? `${fmtOne(r.ica_fte)} of ${fmtOne(r.lecturer_fte)} FTE`
            : null,
        breach: !withheld && r.breach === true,
        withheld,
      };
    })
    .sort(byShare);
}

const AGREED_WITH_FINANCE = /agreed with finance/i;

/** Which targets are the ones agreed with Finance, read from the sources published with them. */
function targetSourcesSentence(fte: IcaGovernanceFte): string {
  const metrics = Array.isArray(fte.metrics) ? fte.metrics.filter(Boolean) : [];
  const sources = fte.target_sources ?? {};
  const agreed = metrics.filter((m) => AGREED_WITH_FINANCE.test(sources[m.key] ?? ''));
  const other = metrics.filter((m) => sources[m.key] && !AGREED_WITH_FINANCE.test(sources[m.key]));
  if (agreed.length && agreed.length === metrics.length) return 'These are the targets agreed with Finance.';
  const parts: string[] = [];
  if (agreed.length) parts.push(`${agreed.length} of the ${metrics.length} are the targets agreed with Finance`);
  if (other.length) {
    parts.push(
      `${other.map((m) => `“${m.metric}”`).join(' and ')} ${
        other.length === 1 ? 'uses the target' : 'use the targets'
      } an earlier board pack published`,
    );
  }
  return parts.length ? `${parts.join('; ')}.` : '';
}

const IcaFteView: React.FC<Props & { fte: IcaGovernanceFte }> = ({ panels, def, fte }) => {
  const threshold = fte.threshold_pct;
  const defs = useMemo<DefLookup>(() => {
    const local = fteDefs(fte.threshold_pct, fte.min_cut_fte);
    return (k: string) => def(k) ?? local[k];
  }, [def, fte.threshold_pct, fte.min_cut_fte]);
  const campusNames = useMemo(() => readCampusNames(panels), [panels]);

  const assessed = useMemo(
    () => assess(Array.isArray(fte.metrics) ? fte.metrics.filter(Boolean) : []),
    [fte],
  );
  const onTarget = assessed.filter((a) => a.status === 'on').length;
  const assessable = assessed.filter((a) => a.status !== 'unknown').length;

  const campusRows = useMemo(() => fteCut(fte.by_campus_fte, 'campus', campusNames), [fte, campusNames]);
  const facultyRows = useMemo(() => fteCut(fte.by_faculty_fte, 'faculty', campusNames), [fte, campusNames]);

  // The finalised pack read lecturers on its own date. Bridge its figure, never restate it.
  const bridge = (Array.isArray(fte.published) ? fte.published : []).find(
    (b) => typeof b?.pack === 'string' && b.pack.startsWith('Finalised'),
  );
  const bridgeLecturers = bridge?.published?.lecturer_fte;
  const bridgeShare = bridge?.published?.ica_share;

  const invoicing = monthName(fte.payroll_month);
  const minCut = fte.min_cut_fte;

  // Taken as published: never the lecturer total less the rows shown.
  const unplacedNote = (unplaced: number | undefined, kind: 'campus' | 'faculty') =>
    typeof unplaced === 'number' && unplaced > 0
      ? `${fmtOne(unplaced)} lecturer FTE sit outside the rows shown — ${
          kind === 'campus'
            ? `a campus carrying under ${minCut} lecturer FTE, or pay with no campus recorded`
            : `a faculty carrying under ${minCut} lecturer FTE, or a lecturer position with no faculty`
        }. They stay in the total.`
      : null;

  const concentrationNote = (m: IcaMetric) => {
    if (typeof m.count !== 'number' || typeof m.of !== 'number') return '';
    const unit = (m.key ?? '').startsWith('facult') ? ' faculties' : (m.key ?? '').startsWith('campus') ? ' campuses' : '';
    return `${m.count} of ${m.of}${unit}`;
  };

  return (
    <PanelShell
      title="Academics — ICA deep dive"
      defKey="ica_governance_fte"
      def={defs}
      window={
        <>
          {panelDateLabel(panels, 'ica_governance_fte')} · lecturer capacity in full-time
          equivalents, lecturer positions as they stood on {fmtDay(fte.establishment_as_at)}. It
          does not follow the basis toggle or the period picker.
        </>
      }
      right={
        assessable ? (
          <span className="text-[11px] text-gray-500">
            <b style={{ color: onTarget === assessable ? ON_TARGET : OFF_TARGET }}>
              {onTarget} of {assessable}
            </b>{' '}
            metrics on target
          </span>
        ) : null
      }
      footnote={
        <>
          Measured in full-time equivalents, the basis agreed with Finance: salaried staff count as
          one, and an independent contractor counts as the share of a full-time equivalent their
          month’s cost represents, capped at one. Cost stands in for teaching hours, so a
          contractor’s share reflects pay rather than hours taught. Earlier packs counted filled
          lecturer positions, which reads a materially higher contractor share. Independent
          contractors are not employees and appear in no employee figure on this page.
          {typeof fte.other_fte === 'number' && fte.other_fte > 0 ? (
            <>
              {' '}
              {fmtOne(fte.other_fte)} lecturer FTE are paid on another payroll type; they are in the
              capacity total.
            </>
          ) : null}
        </>
      }
    >
      <div className="space-y-5">
        {/* ── lecturer capacity ─────────────────────────────────────────── */}
        <div className="space-y-2">
          <TileRow min="150px">
            <MetricTile
              label="Lecturer capacity (FTE)"
              value={fmtOne(fte.lecturer_fte)}
              defKey="ica_lecturer_fte"
              def={defs}
              size="sm"
              sub={`${fmtNum(fte.lecturer_people)} people paid`}
            />
            <MetricTile
              label="Independent contractors"
              value={fmtOne(fte.ica_fte)}
              defKey="ica_fte_contractors"
              def={defs}
              size="sm"
              sub={`${fmtPct(fte.share_pct?.ICA)} of capacity · ${fmtNum(fte.ica_people)} paid`}
            />
            <MetricTile
              label="Permanent"
              value={fmtOne(fte.perm_fte)}
              defKey="ica_fte_permanent"
              def={defs}
              size="sm"
              sub={`${fmtPct(fte.share_pct?.Permanent)} of capacity · ${fmtNum(fte.perm_people)} paid`}
            />
            <MetricTile
              label="Fixed-term (FTC)"
              value={fmtOne(fte.ftc_fte)}
              defKey="ica_fte_ftc"
              def={defs}
              size="sm"
              sub={`${fmtPct(fte.share_pct?.FTC)} of capacity · ${fmtNum(fte.ftc_people)} paid`}
            />
            <MetricTile
              label="Invoiced students"
              value={
                typeof fte.students === 'number' ? (
                  fmtNum(fte.students)
                ) : (
                  <span className="text-gray-400" title="Not available for this date">
                    <span aria-hidden="true">—</span>
                    <span className="sr-only">not available for this date</span>
                  </span>
                )
              }
              defKey="ica_students"
              def={defs}
              size="sm"
              sub={
                typeof fte.students === 'number'
                  ? `${invoicing ?? 'Payroll month'} invoicing, new and returning`
                  : 'not available for this date'
              }
            />
          </TileRow>
          {bridge && typeof bridgeLecturers === 'number' && typeof bridgeShare === 'number' ? (
            <div className="text-[11px] leading-snug text-gray-500">
              <b className="text-gray-600">Since the board pack.</b> The board pack read lecturers on{' '}
              {fmtDay(bridge.read_on)}: {fmtOne(bridgeLecturers)} lecturer FTE, contractor share{' '}
              {fmtPct(bridgeShare)}. {bridge.now_cause}
              {bridge.re_read_differs?.length ? <> {bridge.re_read_cause}</> : null}
            </div>
          ) : null}
        </div>

        {/* ── the metrics, with their targets ──────────────────────────── */}
        <div>
          <div className="mb-1.5 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Metrics against target
            <Def def={defs} k="ica_metric_target" />
          </div>
          <MetricsTable assessed={assessed} def={defs} noteOf={concentrationNote} />
          <div
            className="mt-2 rounded border p-2.5 text-[11px] leading-snug"
            style={{ borderColor: '#E4D6B8', background: '#FBF6EC', color: '#6B4E12' }}
          >
            <b>Recommended targets.</b> {targetSourcesSentence(fte)} An off-target status is a prompt
            for discussion.
          </div>
        </div>

        {/* ── campus cut ──────────────────────────────────────────────── */}
        {campusRows.length ? (
          <CutBlock
            heading="ICA share by campus — highest first"
            defKey="ica_by_campus"
            rows={campusRows}
            threshold={threshold}
            unit="campuses"
            def={defs}
            footer={unplacedNote(fte.campus_unplaced_fte, 'campus')}
          />
        ) : null}

        {/* ── faculty cut ─────────────────────────────────────────────── */}
        {facultyRows.length ? (
          <CutBlock
            heading="ICA share by faculty — highest first"
            defKey="ica_by_faculty"
            rows={facultyRows}
            threshold={threshold}
            unit="faculties"
            def={defs}
            footer={unplacedNote(fte.faculty_unplaced_fte, 'faculty')}
          />
        ) : null}
      </div>
    </PanelShell>
  );
};

// =============================================================================
// SEAT BASIS — `extras.ica_governance`, until it is retired
// =============================================================================

const IcaSeatView: React.FC<Props> = ({ panels, basis, loading, def }) => {
  const defs = useMemo<DefLookup>(() => (k: string) => def(k) ?? SEAT_DEFS[k], [def]);
  const block = readIcaGovernance(panels);
  const campusNames = useMemo(() => readCampusNames(panels), [panels]);

  const lecturers = block?.lecturers ?? null;
  const ica = block?.ica ?? null;
  const perm = block?.perm ?? null;
  const ftc = block?.ftc ?? null;
  const students = block?.students ?? null;

  const metrics = useMemo<IcaMetric[]>(
    () => (Array.isArray(block?.metrics) ? (block?.metrics ?? []).filter(Boolean) : []),
    [block],
  );
  const assessed = useMemo(() => assess(metrics), [metrics]);
  const onTarget = assessed.filter((a) => a.status === 'on').length;
  const assessable = assessed.filter((a) => a.status !== 'unknown').length;

  /**
   * Build one cut. A null never reaches the share, the coverage total or the ordering:
   * rows with no published share sink to the bottom rather than sorting as if they were
   * zero.
   */
  const buildCut = React.useCallback(
    (list: IcaCut[] | null | undefined, kind: 'campus' | 'faculty') => {
      const built = (Array.isArray(list) ? list : []).filter(Boolean).map((r) => {
        const code = ((kind === 'campus' ? r.campus : r.faculty) ?? '').toString();
        const named = kind === 'campus' ? campusNames[code] : undefined;
        const part = r.ica ?? null;
        const whole = r.lecturers ?? null;
        const pct =
          typeof r.ica_pct === 'number' && Number.isFinite(r.ica_pct) ? r.ica_pct : shareOf(part, whole);
        const row: CutRow = {
          key: code || `${kind}-unknown`,
          label: named ? (
            <>
              {named} <span className="text-gray-400">({code})</span>
            </>
          ) : (
            code || '—'
          ),
          pct,
          caption:
            typeof part === 'number' && typeof whole === 'number' ? `${fmtNum(part)} of ${fmtNum(whole)}` : null,
          breach: r.breach === true,
          withheld: false,
        };
        return { row, covered: typeof whole === 'number' ? whole : 0 };
      });
      const covered = built.reduce((s, r) => s + r.covered, 0);
      const unplaced = typeof lecturers === 'number' && built.length ? lecturers - covered : null;
      return { rows: built.map((b) => b.row).sort(byShare), unplaced };
    },
    [campusNames, lecturers],
  );

  const campusCut = useMemo(() => buildCut(block?.by_campus, 'campus'), [block, buildCut]);
  const facultyCut = useMemo(() => buildCut(block?.by_faculty, 'faculty'), [block, buildCut]);

  const unplacedNote = (unplaced: number | null) =>
    unplaced !== null && unplaced > 0
      ? `${fmtNum(unplaced)} lecturer ${unplaced === 1 ? 'position is' : 'positions are'} not placed in this cut — these rows are the cut as published, not a reconciliation of the lecturer total.`
      : null;

  // The three contract legs against the lecturer total — published parts only, so a
  // missing leg leaves the sentence unsaid rather than understating it.
  const legs = [perm, ftc, ica].filter((v): v is number => typeof v === 'number');
  const legTotal = legs.length === 3 ? legs.reduce((s, v) => s + v, 0) : null;
  const unclassified =
    typeof lecturers === 'number' && legTotal !== null && lecturers > legTotal ? lecturers - legTotal : null;

  const caveat = block?.caveat ?? 'Targets are preliminary and not yet benchmarked.';

  return (
    <PanelShell
      title="Academics — ICA deep dive"
      defKey="ica_governance"
      def={defs}
      window={
        block ? (
          <>
            {panelDateLabel(panels, 'ica_governance')} — lecturer <b>positions</b>, not people in
            post, and not a window. It does <b>not</b> follow the basis toggle
            {basis !== 'positions' ? <> — the lecturer establishment is the same on all three bases</> : null}
            ; the period picker only chooses which date carries it. The two student ratios divide
            invoiced students by lecturer positions, so they move with the Finance student file as
            well as with the establishment.
          </>
        ) : (
          'Lecturer capacity, independent contractors against employed lecturers. It does not follow the basis toggle or the period picker.'
        )
      }
      right={
        block && assessable ? (
          <span className="text-[11px] text-gray-500">
            <b style={{ color: onTarget === assessable ? ON_TARGET : OFF_TARGET }}>
              {onTarget} of {assessable}
            </b>{' '}
            metrics on target
          </span>
        ) : null
      }
      footnote={
        block ? (
          <>
            <b>{caveat}</b> They have not been agreed against sector practice or against Eduvos
            history, so this table is a discussion aid rather than a scorecard — the value of an
            off-target chip is the conversation it starts about whether the target is the right one.{' '}
            {unclassified !== null ? (
              <>
                The three contract legs account for {fmtNum(legTotal)} of the {fmtNum(lecturers)}{' '}
                lecturer positions; {fmtNum(unclassified)} {unclassified === 1 ? 'carries' : 'carry'} no
                recoverable contract type.{' '}
              </>
            ) : null}
            Every figure here counts positions rather than people, so the small-group rule that
            withholds person counts elsewhere on this page does not apply — where a figure is missing
            it is drawn as a dash and left out of every total, never read as a zero. ICA lecturers are
            independent contractors: they are not employees, and they appear in no employee figure on
            this page.
          </>
        ) : null
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={420} />
      ) : !panels ? (
        <PanelUnavailable what="The ICA deep dive" />
      ) : !block ? (
        <NotAvailableForDate what="The ICA deep dive" />
      ) : (
        <div className="space-y-5">
          {/* ── the lecturer establishment ───────────────────────────────── */}
          <TileRow min="150px">
            <MetricTile
              label="Lecturer positions"
              value={fmtNum(lecturers)}
              defKey="ica_lecturer_positions"
              def={defs}
              size="sm"
              sub="Seats, filled or vacant"
            />
            <MetricTile
              label="ICA"
              value={fmtNum(ica)}
              defKey="ica_positions"
              def={defs}
              size="sm"
              sub={
                shareOf(ica, lecturers) === null
                  ? 'Share not computable'
                  : `${fmtPct(shareOf(ica, lecturers))} of lecturer seats`
              }
            />
            <MetricTile
              label="Permanent"
              value={fmtNum(perm)}
              defKey="ica_perm_positions"
              def={defs}
              size="sm"
              sub={
                shareOf(perm, lecturers) === null
                  ? 'Share not computable'
                  : `${fmtPct(shareOf(perm, lecturers))} of lecturer seats`
              }
            />
            <MetricTile
              label="Fixed-term (FTC)"
              value={fmtNum(ftc)}
              defKey="ica_ftc_positions"
              def={defs}
              size="sm"
              sub={
                shareOf(ftc, lecturers) === null
                  ? 'Share not computable'
                  : `${fmtPct(shareOf(ftc, lecturers))} of lecturer seats`
              }
            />
            <MetricTile
              label="Invoiced students"
              value={fmtNum(students)}
              defKey="ica_students"
              def={defs}
              size="sm"
              sub="Denominator of both student ratios"
            />
          </TileRow>

          {/* ── the metrics, with their targets ──────────────────────────── */}
          <div>
            <div className="mb-1.5 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Metrics against target
              <Def def={defs} k="ica_metric_target" />
            </div>
            <MetricsTable assessed={assessed} def={defs} noteOf={(m) => m.note ?? ''} />
            <div
              className="mt-2 rounded border p-2.5 text-[11px] leading-snug"
              style={{ borderColor: '#E4D6B8', background: '#FBF6EC', color: '#6B4E12' }}
            >
              <b>Preliminary targets.</b> {caveat} Read the status column as a prompt for
              discussion: an off-target chip may mean the metric needs attention, or that the target
              does.
            </div>
          </div>

          {/* ── campus cut ──────────────────────────────────────────────── */}
          {campusCut.rows.length ? (
            <CutBlock
              heading="ICA share by campus — highest first"
              defKey="ica_by_campus"
              rows={campusCut.rows}
              threshold={SEAT_BREACH_PCT}
              unit="campuses"
              def={defs}
              footer={unplacedNote(campusCut.unplaced)}
            />
          ) : null}

          {/* ── faculty cut ─────────────────────────────────────────────── */}
          {facultyCut.rows.length ? (
            <CutBlock
              heading="ICA share by faculty — highest first"
              defKey="ica_by_faculty"
              rows={facultyCut.rows}
              threshold={SEAT_BREACH_PCT}
              unit="faculties"
              def={defs}
              footer={unplacedNote(facultyCut.unplaced)}
            />
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};

// =============================================================================
// PANEL
// =============================================================================

/**
 * The FTE block leads whenever this date carries it. The seat block is read only in its
 * absence, until it is retired; with neither, the panel says the deep dive is not
 * available for this date.
 */
export const IcaGovernancePanel: React.FC<Props> = (props) => {
  const fte = readExtras(props.panels, 'ica_governance_fte');
  return fte ? <IcaFteView {...props} fte={fte} /> : <IcaSeatView {...props} />;
};
